import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";
import type { InferSchemas } from "../../schema-inference";

// =============================================================================
// Shared item schemas
// =============================================================================

const PackageUsageItemSchema = z.object({
  packageName: z.string(),
  ecosystem: z.enum(["npm", "pip"]),
  totalInstalls: z.number(),
  orgCount: z.number(),
  firstInstalledAt: z.string().datetime(),
  lastInstalledAt: z.string().datetime()
});

// =============================================================================
// Response schemas
// =============================================================================

const PackageUsageSummaryResponseSchema = z.object({
  totalUniquePackages: z.number(),
  topPackages: z.array(PackageUsageItemSchema)
});

const PackageUsageListResponseSchema = z.object({
  items: z.array(PackageUsageItemSchema),
  total: z.number()
});

// =============================================================================
// Params schemas
// =============================================================================

const PackageUsageListParamsSchema = z.object({
  ecosystem: z.enum(["npm", "pip", "all"]).optional().default("all"),
  limit: z.coerce.number().min(1).max(200).optional().default(50),
  offset: z.coerce.number().min(0).optional().default(0)
});

// =============================================================================
// Endpoints
// =============================================================================

export const ZAdminCodeInterpreter = {
  PackagesSummary: {
    method: "GET" as const,
    path: "/api/admin/code-interpreter/packages/summary",
    Response: ApiSuccessSchema(PackageUsageSummaryResponseSchema)
  },
  PackagesList: {
    method: "GET" as const,
    path: "/api/admin/code-interpreter/packages",
    Params: PackageUsageListParamsSchema,
    Response: ApiSuccessSchema(PackageUsageListResponseSchema)
  }
} as const;

export type TApiAdminCodeInterpreter = InferSchemas<typeof ZAdminCodeInterpreter>;
export type PackageUsageItem = z.infer<typeof PackageUsageItemSchema>;
export type PackageUsageSummaryResponse = z.infer<typeof PackageUsageSummaryResponseSchema>;
export type PackageUsageListResponse = z.infer<typeof PackageUsageListResponseSchema>;
